import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, MapPin, Navigation, Plus, Star, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { FavoriteLocationResponse } from '@/lib/api/queries/favorite-locations'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { LoadingSpinner } from '@/components/loading-spinner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useFavoriteLocations } from '@/lib/api/queries/favorite-locations'
import {
  useCreateFavoriteLocation,
  useDeleteFavoriteLocation,
  useUpdateFavoriteLocation,
} from '@/lib/api/mutations/favorite-locations'

export const Route = createFileRoute('/settings/locations')({
  component: LocationsPage,
})

function LocationsPage() {
  const { t } = useTranslation()
  const { data, isLoading, error, refetch } = useFavoriteLocations()
  const createMutation = useCreateFavoriteLocation()
  const updateMutation = useUpdateFavoriteLocation()
  const deleteMutation = useDeleteFavoriteLocation()

  const [showAddForm, setShowAddForm] = useState(false)
  const [editingLocation, setEditingLocation] =
    useState<FavoriteLocationResponse | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null)

  // Form state
  const [formName, setFormName] = useState('')
  const [formLat, setFormLat] = useState('')
  const [formLng, setFormLng] = useState('')
  const [formRadius, setFormRadius] = useState('0.3')
  const [formIsDefault, setFormIsDefault] = useState(false)

  const resetForm = () => {
    setFormName('')
    setFormLat('')
    setFormLng('')
    setFormRadius('0.3')
    setFormIsDefault(false)
  }

  const openAddForm = () => {
    resetForm()
    setEditingLocation(null)
    setShowAddForm(true)
  }

  const openEditForm = (loc: FavoriteLocationResponse) => {
    setFormName(loc.name)
    setFormLat(String(loc.latitude))
    setFormLng(String(loc.longitude))
    setFormRadius(String(loc.radius))
    setFormIsDefault(loc.is_default)
    setEditingLocation(loc)
    setShowAddForm(true)
  }

  const handleUseCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormLat(String(position.coords.latitude.toFixed(6)))
        setFormLng(String(position.coords.longitude.toFixed(6)))
      },
      () => {
        toast.error(t('favoriteLocations.locationError'))
      },
    )
  }

  const handleSubmit = async () => {
    const lat = parseFloat(formLat)
    const lng = parseFloat(formLng)
    const radius = parseFloat(formRadius)

    if (!formName.trim() || isNaN(lat) || isNaN(lng) || isNaN(radius)) {
      toast.error(t('favoriteLocations.validation.required'))
      return
    }

    try {
      if (editingLocation) {
        await updateMutation.mutateAsync({
          locationId: editingLocation.id,
          updates: {
            name: formName,
            latitude: lat,
            longitude: lng,
            radius,
            is_default: formIsDefault,
          },
        })
        toast.success(t('favoriteLocations.updated'))
      } else {
        await createMutation.mutateAsync({
          name: formName,
          latitude: lat,
          longitude: lng,
          radius,
          is_default: formIsDefault,
        })
        toast.success(t('favoriteLocations.created'))
      }
      setShowAddForm(false)
      resetForm()
      setEditingLocation(null)
    } catch {
      toast.error(t('favoriteLocations.error'))
    }
  }

  const handleDelete = async () => {
    if (deleteTarget === null) return
    try {
      await deleteMutation.mutateAsync(deleteTarget)
      toast.success(t('favoriteLocations.deleted'))
    } catch {
      toast.error(t('favoriteLocations.error'))
    }
    setDeleteTarget(null)
  }

  const handleSetDefault = async (loc: FavoriteLocationResponse) => {
    try {
      await updateMutation.mutateAsync({
        locationId: loc.id,
        updates: { is_default: !loc.is_default },
      })
      toast.success(t('common.updated'))
    } catch {
      toast.error(t('favoriteLocations.error'))
    }
  }

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/settings">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">
                {t('favoriteLocations.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('favoriteLocations.description')}
              </p>
            </div>
            <Button size="sm" onClick={openAddForm}>
              <Plus className="h-4 w-4 me-2" />
              {t('favoriteLocations.add')}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {isLoading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {error && (
            <Card>
              <CardContent className="py-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('favoriteLocations.loadError')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => refetch()}
                >
                  {t('common.retry')}
                </Button>
              </CardContent>
            </Card>
          )}

          {!isLoading && !error && data?.locations.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <MapPin className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm font-medium">
                  {t('favoriteLocations.empty')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('favoriteLocations.emptyDescription')}
                </p>
              </CardContent>
            </Card>
          )}

          {data?.locations.map((loc) => (
            <Card key={loc.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {loc.name}
                    {loc.is_default && (
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    )}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleSetDefault(loc)}
                      title={t('favoriteLocations.setDefault')}
                    >
                      <Star
                        className={`h-4 w-4 ${loc.is_default ? 'fill-yellow-400 text-yellow-400' : ''}`}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => setDeleteTarget(loc.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardDescription>
                  {loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)} -{' '}
                  {loc.radius} km
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditForm(loc)}
                >
                  {t('favoriteLocations.edit')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <AlertDialog
        open={showAddForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddForm(false)
            setEditingLocation(null)
            resetForm()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {editingLocation
                ? t('favoriteLocations.editTitle')
                : t('favoriteLocations.addTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('favoriteLocations.formDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('favoriteLocations.name')}</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={t('favoriteLocations.namePlaceholder')}
                maxLength={100}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('favoriteLocations.latitude')}</Label>
                <Input
                  type="number"
                  step="any"
                  value={formLat}
                  onChange={(e) => setFormLat(e.target.value)}
                  placeholder="45.5017"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('favoriteLocations.longitude')}</Label>
                <Input
                  type="number"
                  step="any"
                  value={formLng}
                  onChange={(e) => setFormLng(e.target.value)}
                  placeholder="-73.5673"
                />
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleUseCurrentLocation}
            >
              <Navigation className="h-4 w-4 me-2" />
              {t('favoriteLocations.useCurrentLocation')}
            </Button>
            <div className="space-y-2">
              <Label>{t('favoriteLocations.radius')}</Label>
              <Input
                type="number"
                step="0.1"
                min="0.1"
                max="10"
                value={formRadius}
                onChange={(e) => setFormRadius(e.target.value)}
                placeholder="0.3"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>{t('favoriteLocations.setDefault')}</Label>
              <Switch
                checked={formIsDefault}
                onCheckedChange={setFormIsDefault}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.goBack')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingLocation
                ? t('favoriteLocations.save')
                : t('favoriteLocations.add')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('favoriteLocations.confirmDelete')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('favoriteLocations.confirmDeleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.goBack')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('favoriteLocations.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
